import React, { useMemo } from 'react';

import {
  snackbar,
  ConfirmDialog,
  Typography,
  TableRowActionsMenu,
  useConfirmDialog,
} from '@occc-app/design-system';
import { useOfferDeleteMutation } from '@occc-app/dataAccessLayer/offer/mutations';

interface CampaignWithComponentTableRowActionsContainerProps {
  offerId: string;
  name: string;
  onEdit: () => void;
  shouldShowEditItem: boolean;
  shouldShowArchiveItem: boolean;
}

export const CampaignWithComponentTableRowActionsContainer: React.FC<
  CampaignWithComponentTableRowActionsContainerProps
> = ({ offerId, name, onEdit, shouldShowEditItem, shouldShowArchiveItem }) => {
  const onDeleteComplete = () => {
    snackbar.success('Campaign Component has been successfully archived.');
  };
  const [offerDelete] = useOfferDeleteMutation({
    onCompleted: onDeleteComplete,
    refetchQueries: ['campaignsWithCampaignComponents'],
  });

  const onConfirm = () =>
    offerDelete({
      variables: { id: offerId },
    });

  const { open, setOpen, onCloseButtonClick, onConfirmButtonClick } = useConfirmDialog({
    onConfirm,
    shouldStopEventPropagation: true,
  });

  const menuItems = useMemo(() => {
    const items = [];

    if (shouldShowEditItem) {
      items.push({
        label: 'Edit Component',
        onClick: onEdit,
        dataTestId: 'edit',
      });
    }

    if (shouldShowArchiveItem) {
      items.push({
        label: 'Archive Component',
        onClick: () => setOpen(true),
        dataTestId: 'archive',
      });
    }

    return items;
  }, [shouldShowEditItem, shouldShowArchiveItem, onEdit, setOpen]);

  return (
    <>
      <TableRowActionsMenu menuItems={menuItems} />
      <ConfirmDialog
        dialogTitle='Are you sure?'
        open={open}
        onClose={onCloseButtonClick}
        confirmButtonText='Confirm'
        confirmButtonProps={{
          onClick: onConfirmButtonClick,
          color: 'error',
        }}
      >
        <Typography>This action will archive the "{name}".</Typography>
      </ConfirmDialog>
    </>
  );
};
